import { motion } from 'framer-motion';
import { FaEthereum } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const getRarityColor = (rarity) => {
  switch (rarity.toLowerCase()) {
    case 'legendary':
      return 'from-yellow-500 to-amber-600';
    case 'epic':
      return 'from-purple-500 to-violet-600';
    case 'rare':
      return 'from-blue-500 to-indigo-600';
    case 'uncommon':
      return 'from-green-500 to-emerald-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};

const getRarityGlow = (rarity) => {
  switch (rarity.toLowerCase()) {
    case 'legendary':
      return 'shadow-yellow-500/50';
    case 'epic':
      return 'shadow-purple-500/50';
    case 'rare':
      return 'shadow-blue-500/50';
    case 'uncommon':
      return 'shadow-green-500/50';
    default:
      return 'shadow-gray-500/50';
  }
};

export default function NFTCard({ nft, onClick, showQuickView = true }) {
  const navigate = useNavigate();
  const rarityColor = getRarityColor(nft.rarity);
  const rarityGlow = getRarityGlow(nft.rarity);

  // Handle both backend and mock data formats
  const nftId = nft._id || nft.id;
  const isMinted = nft.status === 'minted' || nft.status === 'transferred' || nft.status === 'sold' || nft.owner;

  const handleClick = () => {
    // Navigate to the NFT details page when the card is clicked
    navigate(`/nft/${nftId}`);
  };

  const handleViewButtonClick = (e) => {
    // Stop propagation to prevent the card click handler from firing
    e.stopPropagation();

    // Navigate to the NFT details page
    navigate(`/nft/${nftId}`);
  };

  const handleQuickViewClick = (e) => {
    // Stop propagation to prevent navigation
    e.stopPropagation();
    e.preventDefault();

    if (onClick) {
      // Call the onClick handler (which will show the modal)
      onClick(nft);
    }
  };

  return (
    <motion.div
      whileHover={{
        y: -10,
        transition: { duration: 0.3 }
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fancy-card cursor-pointer group h-full flex flex-col"
      onClick={handleClick}
    >
      {/* Card Image */}
      <div className="relative overflow-hidden rounded-t-xl aspect-square">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-dark-800/90 z-10"></div>
        <img
          src={nft.image || "/logo.png"}
          alt={nft.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/logo.png";
          }}
        />
        <div className={`absolute top-4 right-4 text-white px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${rarityColor} shadow-lg ${rarityGlow} z-20`}>
          {nft.rarity}
        </div>

        {/* Status badge for minted NFTs */}
        {nft.status && nft.status !== 'draft' && (
          <div className="absolute top-4 left-4 bg-green-900 text-green-300 px-3 py-1 rounded-full text-xs font-semibold z-20">
            {nft.status.charAt(0).toUpperCase() + nft.status.slice(1)}
          </div>
        )}

        {/* Quick View overlay button */}
        {showQuickView && onClick && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
            <button
              onClick={handleQuickViewClick}
              className="px-4 py-2 bg-black/70 hover:bg-black/90 text-white rounded-lg backdrop-blur-sm transition-colors"
              aria-label="Quick preview"
            >
              Quick View
            </button>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 flex-grow flex flex-col">
        <h3 className="text-lg font-bold text-white mb-1 truncate">{nft.name}</h3>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{nft.description}</p>

        <div className="flex justify-between items-center mt-auto">
          <div className="flex items-center">
            <FaEthereum className="text-gray-400 mr-1" />
            <span className="font-semibold text-white">{nft.price}</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleViewButtonClick}
              className="px-3 py-1 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm transition-colors"
            >
              {isMinted ? 'View' : 'Mint'}
            </button>
          </div>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-violet-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"></div>
    </motion.div>
  );
}
